import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCar,
  faBus,
  faTrain,
  faPersonWalking,
  faTaxi,
  faMapMarkedAlt,
  faChevronLeft,
  faTimes,
  faMotorcycle
} from '@fortawesome/free-solid-svg-icons';
import { faGasPump } from '@fortawesome/free-solid-svg-icons';
import { faFilter } from '@fortawesome/free-solid-svg-icons'; // Make sure this is imported
import './TravelOptionsCard.css';

import EpicGoogleMap from './EpicGoogleMap';
import RouteDetailsMotorbike from './RouteDetailsMotorbike';
import RouteDetailsDrive from './RouteDetailsDrive';
import RouteDetailsTaxi from './RouteDetailsTaxi';
import RouteDetailsWalk from './RouteDetailsWalk';
import DepartureTimePopup from './DepartureTimePopup';
import FilterPopup from './FilterPopup';
import RouteDetailsTransit from './RouteDetailsTransit';
import RouteDetailsTransitList from './RouteDetailsTransitList';

const TravelOptionsCard = ({ 
  options, 
  from, 
  to,
  showOverlay,
  setShowOverlay
}) => {
  // Safety check: ensure options is an array
  if (!options || !Array.isArray(options)) {
    console.error('[TravelOptionsCard] Invalid options:', options);
    return null;
  }

  const [overlayData, setOverlayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showDepartPopup, setShowDepartPopup] = useState(false);

  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [showBikeRouteOverlay, setShowBikeRouteOverlay] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showDriveOverlay, setShowDriveOverlay] = useState(false);
  const [showTaxiOverlay, setShowTaxiOverlay] = useState(false);
  const [showWalkOverlay, setShowWalkOverlay] = useState(false);
  const [showPublicRouteOverlay, setShowPublicRouteOverlay] = useState(false);
  const [dTime, setDTime] = useState(""); // optional, defaults to now
const [routingPreference, setRoutingPreference] = useState("BEST_ROUTE");

  // Build Kayak hotel link for the destination
  const buildKayakHotelLink = (city) => {
    if (!city) return "#";
    const creatorId = "760";
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const twoDaysAfter = new Date(today);
    twoDaysAfter.setDate(today.getDate() + 3); // tomorrow + 2 days

    const formatDate = (d) => d.toISOString().split("T")[0];
    const formattedStart = formatDate(tomorrow);
    const formattedEnd = formatDate(twoDaysAfter);

    const specialIds = {
      'Sabah': 'p21083',
      'Pahang': 'p21086',
      'Penang': 'p247880',
      'Sarawak': 'p21082',
      'Kedah': 'p21092',
      'Selangor': 'p21081',
      'Negeri Sembilan': 'p21087',
      'Kelantan': 'p21088'
    };

    let hotelPath;
    if (specialIds[city]) {
      const stateSlug = city.replace(/\s+/g, '-');
      hotelPath = `/hotels/${stateSlug},Malaysia-${specialIds[city]}/${formattedStart}/${formattedEnd}/1adults`;
    } else {
      const citySlug = city.replace(/\s+/g, '-');
      hotelPath = `/hotels/${citySlug},Malaysia/${formattedStart}/${formattedEnd}/1adults`;
    }

    return `https://kayak.com.my/in?a=kan_262812_573418&cid=${creatorId}&lc=en&url=${encodeURIComponent(hotelPath)}`;
  };

  const hotelLink = buildKayakHotelLink(to);

useEffect(() => {
  if (overlayData !== null) {
    setLoading(true);
    const travelOption = "transit"; // Always transit

    // ⏰ Format dTime to "12:03 AM" if it's provided
    let formattedTime = "";
    if (dTime) {
      const date = new Date(dTime);
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const suffix = hours >= 12 ? "PM" : "AM";
      const hour12 = hours % 12 || 12;
      formattedTime = `${hour12}:${minutes} ${suffix}`;
    }

    const dTimeParam = formattedTime ? `&dTime=${encodeURIComponent(formattedTime)}` : "";
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/route/alterativeRouteDetailsSummary?from=${encodeURIComponent(
      from
    )}&to=${encodeURIComponent(to)}&travelOption=${travelOption}&noOfPassengers=1&routingPreference=${routingPreference}${dTimeParam}`;

    const fetchUpdatedRoutes = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const testMode = urlParams.get('testMode') === 'true';
        
        const headers = {};
        if (!testMode) {
          // Add authorization headers if not in test mode
          const token = localStorage.getItem('token');
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        }

        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setOverlayData(json);
      } catch (err) {
        setError(err.message || 'Failed to update route data.');
      } finally {
        setLoading(false);
      }
    };

    fetchUpdatedRoutes();
  }
}, [dTime, routingPreference]);



const handleCardClick = async (option) => {
  const travelOptions = option.travelOptions;

  setOverlayData(null);
  setError(null);
  setSelectedPost(null);
  setLoading(true);

  // Reset overlays
  setShowOverlay(false);
  setShowBikeRouteOverlay(false);
  setShowDriveOverlay(false);
  setShowTaxiOverlay(false);
  setShowWalkOverlay(false);
  setShowPublicRouteOverlay(false);

  const url = `${process.env.REACT_APP_API_BASE_URL}/api/route/alterativeRouteDetailsSummary?from=${encodeURIComponent(
    from
  )}&to=${encodeURIComponent(to)}&travelOption=${travelOptions}&noOfPassengers=1&routingPreference=BEST_ROUTE`;

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const testMode = urlParams.get('testMode') === 'true';
    
    const headers = {};
    if (!testMode) {
      // Add authorization headers if not in test mode
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    setOverlayData(json);

    if (travelOptions === 'drive') {
      setShowDriveOverlay(true);
    } else if (travelOptions === 'taxi') {
      setShowTaxiOverlay(true);
    } else if (travelOptions === 'motorbike') {
      setShowBikeRouteOverlay(true);
    } else if (travelOptions === 'walk') {
      setShowWalkOverlay(true);
    } else {
      setShowOverlay(true); // For transit (bus/train)
    }
  } catch (err) {
    setError(err.message || 'Failed to fetch data.');
    setShowOverlay(true);
  } finally {
    setLoading(false);
  }
};

  const closeOverlay = () => {
    setOverlayData(null);
    setError(null);
    setShowOverlay(false);
    setShowTaxiOverlay(false)
    setShowDriveOverlay(false)
    setSelectedPost(null);
  };

  const getIconForOption = (type) => {
    switch (type) {
      case 'drive': return faCar;
      case 'transit': return faTrain;
      case 'motorbike': return faMotorcycle;
      case 'walk': return faPersonWalking;
      case 'taxi': return faTaxi;
      default: return faCar;
    }
  };


  const getLabelByTravelOption = (type) => {
    switch (type) {
      case 'drive': return "Drive";
      case 'transit': return "Public Transport";
      case 'motorbike': return "Motorbike";
      case 'walk': return "Walk";
      case 'taxi': return "Taxi";
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'taxi': return '#FFD700';
      case 'transit': return '#FF6B00';
      case 'motorbike': return '#333';
      case 'walk': return '#2E8B57';
      case 'drive': return '#444';
      default: return '#666';
    }
  };

  const handleRouteCardClick = (route) => {
    setSelectedRoute(route);
    setShowPublicRouteOverlay(true);
  };
  const getTransitIcon = (name) => {
    if (/ETS|KTM|komuter/i.test(name)) return faTrain;
    if (/LRT|MRT|monorail|subway/i.test(name)) return faTrain; // you can customize this
    return faBus;
  };
  
  return (
    <div>
      <div className="card-list">
        {options.map((opt, idx) => (
          <div
            key={idx}
            onClick={() => handleCardClick(opt)}
            className="mode-card"
          >
            <div className="mode-left">
              <div className="mode-icon">
                <FontAwesomeIcon
                  icon={getIconForOption(opt.travelOptions)}
                  style={{ color: '#2A3075' }}
                />
              </div>
              <div className="mode-info">
                <div className="mode-name">
                  {getLabelByTravelOption(opt.travelOptions)}
                  {/* {opt.travelOptions === 'taxi' && (
                    <span className="taxi-airasia-tag">Airasia ride</span>
                  )} */}
                </div>
                <div className="mode-duration">
                  {opt.duration}
                </div>
              </div>
            </div>

            <div className="mode-right">
              {opt.travelOptions === 'drive' && (
                <FontAwesomeIcon 
                  icon={faGasPump} 
                  className="fuel-icon"
                />
              )}
              <span className="fare">
                {opt.fare}
              </span>
            </div>
          </div>
        ))}
        
      </div>

      {/* Hotel Search Section */}
      {to && (
        <div className="hotel-section">
          <div className="hotel-desc">
            Get recommendations for best places to stay and see. <b>Start here!</b>
          </div>
          <a
            className="hotel-open-btn"
            href={hotelLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            🏨 View Hotels in {to}
          </a>
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          Loading...
        </div>
      )}

      {showOverlay && (
        <div className="overlay">
          <div className="overlay-header">
            <div className="overlay-title">Route Details</div>
            <button 
              onClick={closeOverlay}
              className="close-button"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="overlay-content">
            <RouteDetailsTransitList
              from={from}
              to={to}
              overlayData={overlayData}
              handleRouteCardClick={handleRouteCardClick}
              setShowDepartPopup={setShowDepartPopup}
              setShowFilterPopup={setShowFilterPopup}
              loading={loading}
              error={error}
              closeOverlay={closeOverlay}
            />
          </div>
        </div>
      )}

      {showDepartPopup && (
        <DepartureTimePopup
          dTime={dTime}
          setDTime={setDTime}
          setShowDepartPopup={setShowDepartPopup}
        />
      )}

      {showFilterPopup && (
        <FilterPopup
          routingPreference={routingPreference}
          setRoutingPreference={setRoutingPreference}
          setShowFilterPopup={setShowFilterPopup}
        />
      )}

      {showBikeRouteOverlay && overlayData && Array.isArray(overlayData) && (
        
        <RouteDetailsMotorbike
          from={from}
          to={to}
          selectedRoute={overlayData[0]}
          setShowBikeRouteOverlay={setShowBikeRouteOverlay}
          setSelectedRoute={setSelectedRoute}
        />
      )}

      {showDriveOverlay && overlayData && Array.isArray(overlayData) && (
        <RouteDetailsDrive
          from={from}
          to={to}
          selectedRoute={overlayData[0]}
          setShowDriveOverlay={setShowDriveOverlay}
          setSelectedRoute={setSelectedRoute}
        />
      )}

      {showTaxiOverlay && overlayData && Array.isArray(overlayData) && (
        <RouteDetailsTaxi
          from={from}
          to={to}
          selectedRoute={overlayData[0]}
          setShowTaxiOverlay={setShowTaxiOverlay}
          setSelectedRoute={setSelectedRoute}
        />
      )}

      {showWalkOverlay && overlayData && Array.isArray(overlayData) && (
        <RouteDetailsWalk
          from={from}
          to={to}
          selectedRoute={overlayData[0]}
          setShowWalkOverlay={setShowWalkOverlay}
          setSelectedRoute={setSelectedRoute}
        />
      )}

      {showPublicRouteOverlay && selectedRoute && (
        <RouteDetailsTransit
          from={from}
          to={to}
          selectedRoute={selectedRoute}
          setShowPublicRouteOverlay={setShowPublicRouteOverlay}
          setSelectedRoute={setSelectedRoute}
          getTransitIcon={getTransitIcon}
        />
      )}
    </div>
  );
};

export default TravelOptionsCard;